import { eq } from "drizzle-orm";
import {
  customer,
  loyalty_movement,
  order,
  type PowerSyncDrizzleDB,
  promotion,
} from "@comandero/powersync";
import { nowISO } from "../utils/date-helpers";
import { generateUUID } from "../utils/uuid";
import type { PromotionKind } from "./calc";

// ─── Clientes ─────────────────────────────────────────────────────────────────

/** Crea un cliente. `points` es saldo derivado server-side (no se escribe aquí). */
export async function createCustomerMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  name: string,
  opts?: { phone?: string; email?: string },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(customer).values({
    id,
    organization_id: organizationId,
    name: name.trim(),
    phone: opts?.phone?.trim() || null,
    email: opts?.email?.trim() || null,
    points: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deleteCustomerMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  const now = nowISO();
  await db.update(customer).set({ deleted_at: now, updated_at: now }).where(eq(customer.id, id));
}

/**
 * Asocia un cliente a una orden (para acumular puntos al cobrar). CC-12:
 * reenvía branch_id en todo update de `order`.
 */
export async function setOrderCustomerMutation(
  db: PowerSyncDrizzleDB,
  orderId: string,
  customerId: string | null,
): Promise<void> {
  const current = await db
    .select({ branch_id: order.branch_id })
    .from(order)
    .where(eq(order.id, orderId))
    .limit(1);
  await db
    .update(order)
    .set({
      customer_id: customerId,
      branch_id: current[0]?.branch_id ?? null,
      updated_at: nowISO(),
    })
    .where(eq(order.id, orderId));
}

// ─── Fidelización (canje de puntos) ───────────────────────────────────────────

export interface RedeemPointsResult {
  /** Movimiento de canje creado (puntos negativos). */
  movementId: string;
  /** Descuento aplicado a la orden, en centavos. */
  discountCents: number;
}

/**
 * Canjea `pointsToRedeem` puntos del cliente como descuento de la orden.
 *
 * Escribe un `loyalty_movement` reason='redeem' (puntos negativos): el saldo del
 * cliente lo recalcula el write-path server-side de forma idempotente. Aplica el
 * descuento al `discount` de la orden y registra `points_redeemed`. El cálculo
 * de puntos→centavos es PURO (calc.ts). CC-7 (await) + CC-12 (branch_id).
 */
export async function redeemPointsForOrderMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  orderId: string,
  customerId: string,
  pointsToRedeem: number,
  discountCents: number,
): Promise<RedeemPointsResult> {
  const points = Math.max(0, Math.trunc(pointsToRedeem));
  const discount = Math.max(0, Math.trunc(discountCents));
  const now = nowISO();

  const movementId = generateUUID();
  await db.insert(loyalty_movement).values({
    id: movementId,
    organization_id: organizationId,
    customer_id: customerId,
    order_id: orderId,
    points_delta: -points,
    reason: "redeem",
    created_at: now,
    updated_at: now,
  });

  // Aplica el descuento de canje a la orden (sumado al descuento existente,
  // acotado al subtotal). CC-12: reenvía branch_id.
  const current = await db.select().from(order).where(eq(order.id, orderId)).limit(1);
  const subtotal = current[0]?.subtotal ?? 0;
  const tax = current[0]?.tax_total ?? 0;
  const newDiscount = Math.min(discount, subtotal);
  const newTotal = Math.max(0, subtotal - newDiscount + tax);
  await db
    .update(order)
    .set({
      customer_id: customerId,
      discount: newDiscount,
      total: newTotal,
      points_redeemed: points,
      branch_id: current[0]?.branch_id ?? null,
      updated_at: now,
    })
    .where(eq(order.id, orderId));

  return { movementId, discountCents: newDiscount };
}

// ─── Promociones ──────────────────────────────────────────────────────────────

export async function createPromotionMutation(
  db: PowerSyncDrizzleDB,
  organizationId: string,
  name: string,
  kind: PromotionKind,
  value: number,
  opts?: { startHour?: number | null; endHour?: number | null },
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(promotion).values({
    id,
    organization_id: organizationId,
    name: name.trim(),
    kind,
    value: Math.trunc(value),
    start_hour: opts?.startHour ?? null,
    end_hour: opts?.endHour ?? null,
    is_active: 1,
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function deletePromotionMutation(db: PowerSyncDrizzleDB, id: string): Promise<void> {
  const now = nowISO();
  await db.update(promotion).set({ deleted_at: now, updated_at: now }).where(eq(promotion.id, id));
}
